// ============================================================
// fondos/lienzo.js — LA BASE de los efectos de lienzo (DOM)
//
// P3 del motor de fondos, fase F2b (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.2-2.3). Cada clase de `vendor/bg/` traía su canvas, su medida, su bucle, su throttle y su
// escucha del ratón, y las 9 copias fallaban igual: ninguna aplicaba el DPR (borrosas en retina,
// E8), ninguna usaba `dt` (la velocidad dependía de la pantalla y de la carga, E7) y el ratón lo
// escuchaban en un canvas con `pointer-events:none` que nunca lo oía (E12). Aquí va UNA vez lo que
// es de la técnica, y el efecto (`fondos/efectos/<id>.js`) solo dibuja:
//
//   · MEDIDA Y DPR. El lienzo mide la caja de su capa (tamaño de maqueta: una animación de entrada
//     con `transform` no lo engaña) y pinta a `min(devicePixelRatio, 2)` —con un tope de píxeles por
//     lienzo—, salvo los SUAVES (bokeh), que ya son desenfocados y se quedan a 1, y los de
//     resolución BAJA (blobs, mesh y humo: `nitidez: 'baja'` + su `divisor`), que pintan a
//     1/divisor y el navegador los estira: el filo sale suave y pintar, barato (como sus clases de
//     `vendor/bg/`). El efecto dibuja siempre en px CSS: la escala la pone el lienzo.
//   · LIMPIEZA. Antes de cada fotograma, salvo en los efectos con ESTELA (matrix), que tienden su
//     propio velo sobre lo anterior.
//   · TIEMPO. `t` (ms) es el reloj del efecto: avanza solo lo que el motor le da con `pintar(dt)`.
//     Nadie lee el reloj de la página ni crea temporizadores: el ritmo es del motor (bucle único).
//   · FOTOGRAMA QUIETO (presupuesto, reducir movimiento, gama baja, pestaña oculta). Repinta el
//     estado tal cual; la primera vez, PRECALENTADO si el efecto lo pide (matrix: su lluvia tarda en
//     llenar la sección, y quieta en el primer paso sería una sección casi vacía).
//   · PUNTERO. Para los fondos con `interaccion` (neural, particles, dot-grid), un `pointermove`
//     pasivo en la SECCIÓN; en táctil no se escucha. La capa sigue en `pointer-events:none`.
//
// Lo usa SOLO el motor (`fondos/motor.js`), que decide cuándo anima, cuándo queda quieto y cuándo se
// re-mide. No importa nada con DOM al cargarse (ni `motion-tokens.js`: el motor le pasa `tactil`),
// así que `dprDelLienzo` se prueba en node.
// ============================================================

import { PASO } from './efectos/comun.js';

/** El DPR más alto al que se pinta: por encima, más memoria y más relleno sin que se note. */
export const DPR_MAX = 2;

/**
 * Tope de píxeles de UN lienzo (6 M ≈ 24 MB en RGBA, el mismo umbral que el plan pone a las capas de
 * una sección). Una sección muy alta a DPR 2 lo pasaría (1440×1600 CSS → 9,2 M): entonces el DPR
 * baja lo justo, nunca por debajo de 1 (la resolución de siempre).
 */
export const TOPE_PIXELES = 6000000;

/** Tope de vueltas del precalentado (un seguro: ningún efecto se acerca). */
const PRECALENTADO_MAX = 2000;

const ESTILO_CANVAS = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:0;';

/**
 * El DPR al que se pinta un lienzo de `w`×`h` px CSS.
 * @param {number} w
 * @param {number} h
 * @param {number} dispositivo - `window.devicePixelRatio`
 * @param {string} [nitidez] - 'suave' → 1; 'baja' → 1/divisor
 * @param {number} [divisor] - con 'baja', cuántos px CSS por px de lienzo (1 si falta o es < 1)
 */
export function dprDelLienzo(w, h, dispositivo, nitidez, divisor) {
    if (nitidez === 'baja') return 1 / (divisor >= 1 ? divisor : 1);
    if (nitidez === 'suave') return 1;
    const base = Math.min(dispositivo > 0 ? dispositivo : 1, DPR_MAX);
    if (w * h * base * base <= TOPE_PIXELES) return base;
    return Math.max(Math.min(1, base), Math.sqrt(TOPE_PIXELES / (w * h)));
}

function crearLienzo(ancho, alto) {
    const c = document.createElement('canvas');
    c.width = ancho;
    c.height = alto;
    return c;
}

/**
 * Monta el efecto `efecto` en un canvas nuevo dentro de `contenedor` (el `.bg-layer`).
 *
 * @param {{crear: Function, redimensionar: Function, dibujar: Function, destruir?: Function,
 *          nitidez?: string, divisor?: number, estela?: boolean, precalentado?: number|Function}} efecto
 * @param {HTMLElement} contenedor
 * @param {{c1: string, oscuro: boolean, params: Object, tactil: boolean, alfa?: number,
 *          seccion?: HTMLElement, interaccion?: boolean}} datos
 *   `params` = `efectos/index.js::opcionesDelEfecto` (tal cual, sin defectos: cada efecto aplica los
 *   suyos); `tactil` = pantalla táctil sin ratón (`motion-tokens.js::IS_TOUCH`); `alfa` = el tope de
 *   alfa del fondo en esa polaridad (`catalogo.js::alfaMaxDe`), del que el efecto saca sus alfas;
 *   `seccion` = donde se escucha el puntero si el fondo tiene `interaccion`.
 * @returns {{tecnica: 'lienzo', lienzo: HTMLCanvasElement, animar: Function, parar: Function,
 *            pintar: function(number): void, fotograma: Function, redimensionar: Function,
 *            destruir: Function}}
 */
export function montarLienzo(efecto, contenedor, datos) {
    const canvas = document.createElement('canvas');
    canvas.className = 'bg-canvas';
    canvas.style.cssText = ESTILO_CANVAS;
    contenedor.appendChild(canvas);
    const ctx = canvas.getContext('2d', { alpha: true });

    let w = 1;
    let h = 1;
    medir();

    const estado = efecto.crear(ctx, {
        w: w,
        h: h,
        oscuro: datos.oscuro == null ? true : !!datos.oscuro,
        c1: datos.c1 || '#6366f1',
        params: datos.params || {},
        tactil: !!datos.tactil,
        alfa: typeof datos.alfa === 'number' ? datos.alfa : 1,
        // Leído en cada llamada (no capturado): los arneses siembran `Math.random`.
        azar: function () { return Math.random(); },
        crearLienzo: crearLienzo,
    });

    let t = 0;
    let animando = false;
    let conDibujo = false;
    // El fotograma quieto necesita precalentado: el estado está recién creado (o, con estela, el
    // lienzo se quedó en blanco al re-medirse).
    let porPrecalentar = true;

    // ── Puntero (solo los fondos con `interaccion`, nunca en táctil) ──
    let cliente = null;
    let alMover = null;
    let alSalir = null;
    if (datos.interaccion && !datos.tactil && datos.seccion) {
        alMover = function (ev) {
            if (ev.pointerType === 'touch') return;
            cliente = { x: ev.clientX, y: ev.clientY };
        };
        alSalir = function () { cliente = null; };
        datos.seccion.addEventListener('pointermove', alMover, { passive: true });
        datos.seccion.addEventListener('pointerleave', alSalir, { passive: true });
    }

    /** El puntero en px CSS del lienzo (se resuelve al pintar: vale también tras un scroll). */
    function puntero() {
        if (!cliente) return null;
        const r = canvas.getBoundingClientRect();
        if (!r.width || !r.height) return null;
        return { x: (cliente.x - r.left) * (w / r.width), y: (cliente.y - r.top) * (h / r.height) };
    }

    function medir() {
        w = Math.max(1, contenedor.clientWidth || 0);
        h = Math.max(1, contenedor.clientHeight || 0);
        const dpr = dprDelLienzo(w, h, window.devicePixelRatio || 1, efecto.nitidez, efecto.divisor);
        canvas.width = Math.max(1, Math.round(w * dpr));
        canvas.height = Math.max(1, Math.round(h * dpr));
        // Redimensionar un canvas le devuelve la transformación a la identidad: se pone cada vez.
        ctx.setTransform(canvas.width / w, 0, 0, canvas.height / h, 0, 0);
    }

    function dibujar(dt, conPuntero) {
        if (!efecto.estela) ctx.clearRect(0, 0, w, h);
        efecto.dibujar(estado, ctx, t, dt, conPuntero ? puntero() : null);
        conDibujo = true;
    }

    function precalentar() {
        porPrecalentar = false;
        const pre = efecto.precalentado;
        const ms = typeof pre === 'function' ? pre(estado) : (pre || 0);
        const n = Math.min(Math.ceil(ms / PASO), PRECALENTADO_MAX);
        for (let i = 0; i < n; i++) {
            t += PASO;
            dibujar(PASO, false);
        }
    }

    return {
        tecnica: 'lienzo',
        lienzo: canvas,
        /** Anima desde donde esté: los fotogramas los pide el motor, desde su bucle único. */
        animar: function () {
            animando = true;
            porPrecalentar = false;
        },
        parar: function () {
            animando = false;
        },
        /** Un fotograma animado: el reloj del efecto avanza `dt` ms. */
        pintar: function (dt) {
            porPrecalentar = false;
            t += dt;
            dibujar(dt, true);
        },
        /** Un fotograma QUIETO: el estado tal cual (precalentado la primera vez, si el efecto lo pide). */
        fotograma: function () {
            if (porPrecalentar) precalentar();
            dibujar(0, false);
        },
        /**
         * Re-mide la caja SIN reiniciar el efecto (E6): el lienzo sigue a la sección. Redimensionar un
         * canvas lo deja en blanco, así que se repinta al momento (sin esperar al bucle); con estela,
         * lo pintado se perdió y el siguiente fotograma quieto vuelve a precalentar.
         */
        redimensionar: function () {
            medir();
            efecto.redimensionar(estado, w, h);
            if (efecto.estela) porPrecalentar = true;
            else if (conDibujo) dibujar(0, animando);
        },
        destruir: function () {
            if (alMover) {
                datos.seccion.removeEventListener('pointermove', alMover);
                datos.seccion.removeEventListener('pointerleave', alSalir);
            }
            if (typeof efecto.destruir === 'function') efecto.destruir(estado);
            canvas.remove();
        },
    };
}
